import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { FormikHelpers } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import FormWrapper from 'components/common/formikForm/formWrapper';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import type { CalendarReminderFormInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    calendarReminderDefaultValues,
    calendarReminderValidationSchema,
} from 'utils/validations/calendarReminder';

interface CalendarReminderPageProps {
    onClose: () => void;
    appId: string;
}

const CalendarReminderPage: React.FC<CalendarReminderPageProps> = ({ onClose, appId }) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(false);

    const handleSubmit = async (
        values: CalendarReminderFormInterface,
        _helpers: FormikHelpers<CalendarReminderFormInterface>
    ) => {
        setLoading(true);
        try {
            const payload = { ...values, app_id: appId };

            const response = await api.calendar_events.create_calendar_event(payload);

            if (response.status === 201) {
                dispatch(
                    setAlert({
                        message: translate('eventCreatedSuccessfully'),
                        type: 'success',
                    })
                );
                onClose();
                return;
            }

            dispatch(
                setAlert({
                    message: translate('eventCreateFailed'),
                    type: 'error',
                })
            );
        } catch (error: unknown) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('eventCreateFailed')),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <Dialog
                open
                fullWidth
                maxWidth="sm"
                scroll="paper"
                fullScreen={isMobile}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'hidden',
                            '& > form': {
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '1 1 auto',
                                minHeight: 0,
                                overflow: 'hidden',
                            },
                        },
                    },
                }}
            >
                <DialogTitle>
                    {translate('scheduleFollowUp')}
                    <Typography variant="body2" color="text.secondary">
                        {translate('scheduleFollowUpSubtitle')}
                    </Typography>
                </DialogTitle>

                <FormWrapper<CalendarReminderFormInterface>
                    initialValues={calendarReminderDefaultValues}
                    validationSchema={calendarReminderValidationSchema}
                    onSubmit={handleSubmit}
                >
                    <DialogContent
                        dividers
                        sx={{ pt: 3, pb: 1, flex: '1 1 auto', overflowY: 'auto' }}
                    >
                        <Stack spacing={0.5}>
                            <FormField
                                required
                                size="small"
                                name="title"
                                label="eventTitle"
                                disabled={loading}
                            />

                            <FormGridLayout>
                                <FormField
                                    required
                                    size="small"
                                    type="date"
                                    name="date"
                                    label="eventDate"
                                    disabled={loading}
                                />
                                <FormField
                                    required
                                    size="small"
                                    type="time"
                                    name="time"
                                    label="eventTime"
                                    disabled={loading}
                                />
                            </FormGridLayout>

                            <FormField
                                size="small"
                                name="description"
                                label="eventDescription"
                                multiline
                                minRows={4}
                                disabled={loading}
                            />

                            <FormField
                                required
                                size="small"
                                name="timezone"
                                label="timezone"
                                disabled
                            />

                            <FormField
                                type="checkbox"
                                size="small"
                                name="add_all_agents"
                                label="addAllAgents"
                                description="addAllAgentsDescription"
                                disabled={loading}
                            />
                            <FormField
                                type="checkbox"
                                size="small"
                                name="add_client"
                                label="addClient"
                                description="addClientDescription"
                                disabled={loading}
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            flexShrink: 0,
                            px: { xs: 2, sm: 3 },
                            py: 1.5,
                            gap: 1,
                            flexDirection: 'row',
                            '& > :not(style)': {
                                width: { xs: '100%', sm: 'auto' },
                                m: '0 !important',
                            },
                        }}
                    >
                        <Button
                            onClick={onClose}
                            color="inherit"
                            disabled={loading}
                            sx={{ textTransform: 'initial' }}
                        >
                            {translate('close')}
                        </Button>
                        <SubmitButton loading={loading}>
                            {loading ? 'creating' : 'createEvent'}
                        </SubmitButton>
                    </DialogActions>
                </FormWrapper>
            </Dialog>
        </ThemeProvider>
    );
};

export default CalendarReminderPage;
